import { useState } from "react";
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTrackingProducts } from "@/hooks/useTrackingProducts";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

interface HardwareMarketplaceProps {
  companyId: string;
  companySlug?: string;
}

const planTypeLabels = {
  basic: 'Básico',
  standard: 'Padrão',
  premium: 'Premium'
};

export function HardwareMarketplace({ companyId }: HardwareMarketplaceProps) {
  const { data: devices, isLoading } = useTrackingProducts('gps_device');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const { addItem } = useCart();

  const handlePurchase = (device: any) => {
    const quantity = quantities[device.id] || 1;
    addItem({
      type: 'gps_tracker',
      productId: device.id,
      productName: device.name,
      description: device.description,
      quantity,
      unitPrice: device.price_cents,
    });
    toast.success(`${device.name} adicionado ao carrinho`);
  };

  if (isLoading) return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  if (!devices?.length) return <Card className="p-8 text-center"><p className="text-muted-foreground">Nenhum dispositivo disponível</p></Card>;

  return (
    <div className="space-y-6">
      <div><h2 className="text-2xl font-bold">Dispositivos GPS</h2><p className="text-muted-foreground">Dispositivos com plano de dados incluso</p></div>
      <div className="space-y-3">
        {devices.map((device) => (
          <Card key={device.id} className="p-4">
            <div className="flex items-center gap-4">
              {device.image_url ? <img src={device.image_url} alt={device.name} className="w-20 h-20 object-cover rounded-lg border border-border" /> : <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center"><ShoppingCart className="h-8 w-8 text-muted-foreground" /></div>}
              <div className="flex-1">
                <div className="flex justify-between"><div><h3 className="font-semibold text-lg">{device.name}</h3><p className="text-sm text-muted-foreground">{device.model}</p></div><p className="text-2xl font-bold text-primary">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(device.price_cents / 100)}</p></div>
                {device.description && <p className="text-sm text-muted-foreground mt-1">{device.description}</p>}
                {device.data_plan_name && <div className="flex items-center gap-2 mt-2">{device.data_plan_type && <Badge variant="outline" className="text-xs">{planTypeLabels[device.data_plan_type as keyof typeof planTypeLabels]}</Badge>}<span className="text-sm font-medium">{device.data_plan_name}</span></div>}
              </div>
              <div className="flex items-center gap-3"><Input type="number" min="1" value={quantities[device.id] || 1} onChange={(e) => setQuantities(prev => ({ ...prev, [device.id]: parseInt(e.target.value) || 1 }))} className="w-20" /><Button onClick={() => handlePurchase(device)}><ShoppingCart className="h-4 w-4 mr-2" />Comprar</Button></div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
